import { IS_WEB } from 'constants/platform'
import React from 'react'
import { StyleSheet } from 'react-native'
import { v4 as uuidv4 } from 'uuid'

import { Avatar } from 'widgets/Avatar'
import { Box } from 'widgets/Box'
import { Icon } from 'widgets/Icon'
import { Image } from 'widgets/Image'
import { Text } from 'widgets/Text'

import { useGroup } from 'hooks/useGroup'
import { useMaxWidth } from 'hooks/useMaxWidth'
import { useUser } from 'hooks/useUser'

import { defaultPostThumbnailUrl } from 'utils/media'
import commonStyles from 'utils/styles/commonStyles'

import AthleticGreensIcon from 'assets/sponsors/sponsor-athletic-greens.png'
import HypericeIcon from 'assets/sponsors/sponsor-hyperice.png'

type CoachReferralSponsoredViewProps = {
  data: any
}

const styles = StyleSheet.create({
  mh500: {
    maxHeight: 500,
  },
  avatarBorder: {
    borderRadius: 20,
  },
})

export const CoachReferralSponsoredView = ({ data }: CoachReferralSponsoredViewProps) => {
  const maxWidth = useMaxWidth()
  const { user, group } = data

  const { data: challengeGroupUser } = useUser({ username: 'challenges' })
  const { data: challengeGroup } = useGroup({ userId: challengeGroupUser?.id })

  return (
    <Box align='center' margin={{ horizontal: 'md' }}>
      <Box
        margin={{ top: 'md' }}
        justifyContent='center'
        align='center'
        width='100%'
        height={maxWidth.width * 1.2}
        style={styles.mh500}
      >
        <Box flexDirection='row' align='center' justifyContent='center'>
          <Box
            width={42}
            height={42}
            backgroundColor='greyDarkest'
            align='center'
            justifyContent='center'
            style={styles.avatarBorder}
            border={{ width: 1, color: 'v2midGrey' }}
          >
            <Avatar width={40} height={40} image={user?.avatar} />
          </Box>
          <Box margin={{ left: 'smd' }}>
            <Text bold fontSize='sm' color='white'>
              {`${user?.username} `}
              <Text fontSize='sm' color='white'>
                invited you to join
              </Text>
            </Text>
          </Box>
        </Box>

        <Box width='100%' flexDirection='row' margin={{ vertical: 'lg' }} justifyContent='center'>
          <Box flex={1} align='center'>
            <Box
              width={maxWidth.width * 0.32}
              height={maxWidth.width * 0.32}
              borderRadius='sm'
              backgroundColor='greyDarker'
              style={commonStyles.overflowHidden}
            >
              <Box position='absolute' width='100%' height='100%' backgroundColor='greyLight'>
                <Image
                  cacheKey={
                    group
                      ? // eslint-disable-next-line no-underscore-dangle
                        `${group?.id}-group-cover-${group?.cover?._original?.etag}-large`
                      : uuidv4()
                  }
                  resizeMode='cover'
                  style={commonStyles.fullSize}
                  source={
                    IS_WEB
                      ? { uri: group?.cover?.url ?? defaultPostThumbnailUrl }
                      : group?.cover ?? defaultPostThumbnailUrl
                  }
                />
              </Box>
            </Box>
            <Box width='100%' margin={{ vertical: 'sm' }} align='center'>
              <Text bold color='white' align='center' fontSize='sm' numberOfLines={2}>
                {group?.title}
              </Text>
              <Text color='greyLight' fontSize='sm'>
                {user?.username}
              </Text>
            </Box>
          </Box>

          <Box height='75%' align='center' justifyContent='center'>
            <Icon name='plus' size={50} color='white' />
          </Box>

          <Box flex={1} align='center'>
            <Box
              width={maxWidth.width * 0.32}
              height={maxWidth.width * 0.32}
              borderRadius='sm'
              backgroundColor='greyDarker'
              style={commonStyles.overflowHidden}
            >
              <Box position='absolute' width='100%' height='100%' backgroundColor='greyLight'>
                <Image
                  cacheKey={
                    group
                      ? // eslint-disable-next-line no-underscore-dangle
                        `${group?.id}-group-cover-${group?.cover?._original?.etag}-large`
                      : uuidv4()
                  }
                  resizeMode='cover'
                  style={commonStyles.fullSize}
                  source={
                    IS_WEB
                      ? { uri: challengeGroup?.cover?.url ?? defaultPostThumbnailUrl }
                      : challengeGroup?.cover ?? defaultPostThumbnailUrl
                  }
                />
              </Box>
            </Box>
            <Box width='100%' margin={{ vertical: 'sm' }} align='center'>
              <Text bold color='white' align='center' fontSize='sm' numberOfLines={2}>
                {challengeGroup?.title ?? ''}
              </Text>
              <Text color='greyLight' fontSize='sm'>
                {challengeGroupUser?.username ?? ''}
              </Text>
            </Box>
          </Box>
        </Box>

        <Box height={2} width='95%' backgroundColor='v2black' />

        <Box flex={1} width='100%' margin={{ bottom: 'xl' }}>
          <Box flex={1} flexDirection='row' align='flex-end' justifyContent='center'>
            <Text bold color='v2midGrey' align='center' fontSize='sm' numberOfLines={1}>
              Upspace&apos;s {` ${challengeGroup?.title ?? ''} `}
              <Text fontSize='sm' color='v2midGrey'>
                sponsored by
              </Text>
            </Text>
          </Box>

          <Box
            flex={1}
            flexDirection='row'
            justifyContent='center'
            margin={{ top: 'smd', bottom: 'md' }}
          >
            <Box width={maxWidth.width * 0.4}>
              <Image style={commonStyles.fullSize} resizeMode='contain' source={HypericeIcon} />
            </Box>

            <Box margin='sm' />

            <Box width={maxWidth.width * 0.4}>
              <Image
                style={commonStyles.fullSize}
                resizeMode='contain'
                source={AthleticGreensIcon}
              />
            </Box>
          </Box>

          <Box flex={1} flexDirection='row' justifyContent='center'>
            <Text bold color='v2midGrey' fontSize='sm' align='center'>
              The winner of Upspace&apos;s 2023 Challenge gets a free Hypervolt+ and 3 month supply
              of AG1.
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
